
import { useState } from 'react';

interface InvestmentOpportunity {
  title: string;
  location: string;
  investment: string;
  returns: string;
  timeline: string;
  description: string;
  image: string;
  status: string;
  detailedInfo: {
    overview: string;
    marketAnalysis: string[];
    financialProjections: {
      year1: string;
      year3: string;
      year5: string;
      exitStrategy: string;
    };
    riskFactors: string[];
    keyBenefits: string[];
    nextSteps: string[];
  };
  marketAnalysis: string[];
  benefits: string[];
  risks: string[];
  nextSteps: string[];
}

interface InvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: InvestmentOpportunity | null;
}

const InvestmentModal = ({ isOpen, onClose, opportunity }: InvestmentModalProps) => {
  const [showProspectusForm, setShowProspectusForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    investmentAmount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProspectusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('company', formData.company);
      formDataToSubmit.append('investmentAmount', formData.investmentAmount);
      formDataToSubmit.append('message', formData.message);
      formDataToSubmit.append('investmentType', opportunity?.title || '');
      formDataToSubmit.append('recipientEmail', 'b2b@tsdotyeni.com');

      const response = await fetch('https://readdy.ai/api/form/d43md9emj2r470qadfv0', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          investmentAmount: '',
          message: ''
        });
        setTimeout(() => {
          setShowProspectusForm(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleCall = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
    onClose();
  };

  if (!isOpen || !opportunity) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {!showProspectusForm ? (
          <>
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{opportunity.title}</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Investment Overview */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Investment Overview</h3>
                <p className="text-gray-700 leading-relaxed mb-4">{opportunity.description}</p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Expected Returns</h4>
                    <p className="text-green-700">{opportunity.returns}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Investment Timeline</h4>
                    <p className="text-blue-700">{opportunity.timeline}</p>
                  </div>
                </div>
              </div>

              {/* Market Analysis */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Market Analysis</h3>
                <div className="space-y-4">
                  {opportunity.marketAnalysis?.map((point, index) => (
                    <div key={index} className="flex items-start">
                      <i className="ri-check-line text-green-600 mt-1 mr-3"></i>
                      <p className="text-gray-700">{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Benefits */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Key Investment Benefits</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {opportunity.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <i className="ri-star-fill text-yellow-500 mr-3"></i>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Factors */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Risk Assessment</h3>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="space-y-2">
                    {opportunity.risks?.map((risk, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-alert-line text-yellow-600 mt-1 mr-3"></i>
                        <p className="text-yellow-800">{risk}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Next Steps</h3>
                <div className="space-y-3">
                  {opportunity.nextSteps?.map((step, index) => (
                    <div key={index} className="flex items-start">
                      <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
                <button
                  onClick={() => setShowProspectusForm(true)}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Request Detailed Prospectus
                </button>
                <button
                  onClick={handleScheduleCall}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Schedule Due Diligence Call
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Request Investment Prospectus</h2>
                <button
                  onClick={() => setShowProspectusForm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-arrow-left-line text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-600 mt-2">Get detailed information about {opportunity.title}</p>
            </div>

            <div className="p-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <i className="ri-check-circle-line text-6xl text-green-600 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted Successfully!</h3>
                  <p className="text-gray-600">We'll send the detailed prospectus to your email within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleProspectusSubmit} data-readdy-form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Enter your company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-700 mb-2">
                        Potential Investment Amount
                      </label>
                      <select
                        id="investmentAmount"
                        name="investmentAmount"
                        value={formData.investmentAmount}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select investment range</option>
                        <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                        <option value="$100,000 - $500,000">$100,000 - $500,000</option>
                        <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
                        <option value="$1,000,000 - $5,000,000">$1,000,000 - $5,000,000</option>
                        <option value="$5,000,000+">$5,000,000+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your investment goals and any specific questions you have..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-600 mr-2"></i>
                        <p className="text-red-700">There was an error submitting your request. Please try again.</p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowProspectusForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          Submit Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvestmentModal;
